// ─── Impostazioni → 🎁 Premio a chi organizza ────────────────────────────────
//
// Voce 33, dal mockup approvato il 26/09/2026. Crediti, non euro: 1 credito =
// 1,00 € di sconto sulla quota di una partita. Spento di partenza, come la
// caparra: si accende quando il commercialista ha detto la sua.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { VALORE_CREDITO_CENTS, type ImpostazioniPremio } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { SoloLettura, euro, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'premio-impostazioni';

type Modulo = { attivo: boolean; dal: string; crediti: string };

const daSalvata = (s: ImpostazioniPremio): Modulo => ({
  attivo: s.attivo,
  dal: s.dal ?? '',
  crediti: String(s.creditiPerPartita),
});

const intero = (s: string, min: number, max: number): number | undefined => {
  const n = Number(s.trim());
  return Number.isInteger(n) && n >= min && n <= max ? n : undefined;
};

export function Premio(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const letta = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<ImpostazioniPremio>('GET', '/settings/premio'),
  });
  if (letta.isLoading) return <Attesa cosa="le impostazioni del premio" />;
  if (letta.error || !letta.data) return <Guasto errore={letta.error} />;
  return <Pagina salvata={letta.data} modificabile={modificabile} />;
}

function Pagina({
  salvata,
  modificabile,
}: {
  salvata: ImpostazioniPremio;
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [m, setM] = useState<Modulo>(() => daSalvata(salvata));
  const [salvato, setSalvato] = useState(false);
  useEffect(() => setM(daSalvata(salvata)), [salvata]);
  const cambia = (f: (x: Modulo) => Modulo) => {
    setSalvato(false);
    setM(f);
  };

  const crediti = intero(m.crediti, 1, 20);
  const pronto = crediti !== undefined;

  const salva = useMutation({
    meta: { fatto: 'impostazioni del premio salvate' },
    mutationFn: () =>
      chiama<ImpostazioniPremio>('PUT', '/settings/premio', {
        attivo: m.attivo,
        dal: m.dal || null,
        creditiPerPartita: crediti,
      }),
    onSuccess: () => {
      setSalvato(true);
      rileggi();
    },
  });

  return (
    <section>
      <header className="testata">
        <h3>🎁 Premio a chi organizza</h3>
        <p className="tenue">
          Chi organizza una partita e ne ha un’altra, sua, entro 7 giorni (anche la stessa sera) riceve dei
          Crediti per ciascuna. Si vedono subito «in arrivo» e diventano spendibili a fine partita. Valgono
          come sconto sulla sua quota delle partite: non si prelevano, non si rimborsano, non si passano ad
          altri.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      <form
        className="bot-soci"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronto && modificabile) salva.mutate();
        }}
      >
        <fieldset className="riquadro" disabled={!modificabile}>
          <label className="spunta-grande">
            <input
              type="checkbox"
              checked={m.attivo}
              onChange={(e) => cambia((x) => ({ ...x, attivo: e.target.checked }))}
            />{' '}
            <strong>{m.attivo ? 'Acceso' : 'Spento'}</strong>
          </label>
          <p className="tenue piccolo">
            Spento, non nascono Crediti nuovi; quelli già dati restano spendibili. Prima di accenderlo, fai
            confermare al commercialista come va scritto lo sconto sullo scontrino.
          </p>
          <label className="campo">
            <span>Contano le partite giocate dal (vuoto: da oggi)</span>
            <input
              type="date"
              value={m.dal}
              onChange={(e) => cambia((x) => ({ ...x, dal: e.target.value }))}
            />
          </label>
          <Campo
            etichetta="Crediti per ogni partita"
            inputMode="numeric"
            value={m.crediti}
            onChange={(e) => cambia((x) => ({ ...x, crediti: e.target.value }))}
          />
          <p className="tenue piccolo">
            1 Credito = {euro(VALORE_CREDITO_CENTS)} di sconto sulla quota di una partita.
          </p>
          {crediti === undefined && <p className="guasto">Fra 1 e 20 Crediti.</p>}
          {/* 🔄 Voce 36 (27/09/2026): quanto valgono i Crediti sta in una regola sola, con quelli dell'acconto. */}
          <p className="tenue piccolo">
            🔄 Quanto valgono i Crediti del premio, e quando si avvisa il socio: nella linguetta{' '}
            <Link to="/impostazioni/borsellino/crediti">Crediti</Link>, uguale per tutti i Crediti.
          </p>
        </fieldset>

        {modificabile && (
          <>
            <Guasto errore={salva.error} />
            {salvato && !salva.isPending && (
              <p className="tenue">
                Salvato.{' '}
                {m.attivo
                  ? `Da adesso chi organizza riceve ${crediti ?? ''} Crediti per partita.`
                  : 'Il premio è spento.'}
              </p>
            )}
            <button type="submit" disabled={salva.isPending || !pronto}>
              {salva.isPending ? 'Un attimo…' : 'Salva'}
            </button>
          </>
        )}
      </form>
    </section>
  );
}
